import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Debounce a value — returns the debounced version after `delay` ms of inactivity.
 * @param {*} value - The value to debounce
 * @param {number} delay - Debounce delay in ms (default 300)
 */
export function useDebounce(value, delay = 300) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Returns a debounced version of a callback function.
 * The callback will only execute after `delay` ms of inactivity.
 * @param {Function} callback - Function to debounce
 * @param {number} delay - Debounce delay in ms (default 300)
 */
export function useDebouncedCallback(callback, delay = 300) {
  const timerRef = useRef(null);

  const debouncedFn = useCallback((...args) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => callback(...args), delay);
  }, [callback, delay]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return debouncedFn;
}

export default useDebounce;
